import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Button, Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { InputField } from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { ExternalLink } from "@/components/ExternalLink";

const FormSchema = z.object({
  protocol: z.string().nullable().optional(),
  connector: z.string().nullable().optional(),
  path: z.string().nullable().optional(),
  method: z.string().nullable().optional(),
  unzip_strategy: z.string().nullable().optional(),
  file_pattern: z.string().nullable().optional(),
  email_source_type: z.string().nullable().optional(),
  email_subject_user: z.string().nullable().optional(),
});

export function ConnectorConfigsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center h-full w-full relative">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                Connector Config {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField name="protocol" label="Protocol" control={form.control} />
                  <InputField name="connector" label="Connector" control={form.control} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField name="path" label="Path" control={form.control} />
                  <InputField name="method" label="Method" control={form.control} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="file_pattern"
                    label="File Pattern"
                    control={form.control}
                  />
                  <InputField
                    name="unzip_strategy"
                    label="Unzip Strategy"
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="email_source_type"
                    label="Email Source Type"
                    control={form.control}
                  />
                  <InputField
                    name="email_subject_user"
                    label="Email Subject User"
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields ? Diff(changedFields, []) : t("no_changes")}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
      <div className="absolute top-[2.2rem] right-12 2xl:right-10 lg:flex flex-col gap-6 hidden">
        <Button
          href={toWorkspacePath(
            `/admin/qtl - etl/provider_configs?connector_config_id=${id}`,
          )}
          variant="bordered"
          color="primary"
          showAnchorIcon
          as={ExternalLink}
          radius="full"
          className="bg-white border-1 border-neutral-300 shadow-xl"
        >
          Provider Configs
        </Button>
        <Button
          href={toWorkspacePath(
            `/admin/qtl - etl/input_files?connector_config_id=${id}`,
          )}
          variant="bordered"
          color="primary"
          showAnchorIcon
          as={ExternalLink}
          radius="full"
          className="bg-white border-1 border-neutral-300 shadow-xl"
        >
          Input Files
        </Button>
      </div>
    </div>
  );
}
